import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

export const RECOMMENDATION_KEYS = ['strong_yes', 'yes', 'neutral', 'no', 'strong_no'];

// GERİYE DÖNÜK AĞIRLIK KUSURU ROZETİ. Ağırlık kuralı (birim = yüzde, dip toplam
// 100) sonradan eklendi ve var olan satırlar TARANMADI; kural öncesi yazılmış
// şablonlar (ör. 0.8 + 0.1 + 0.1 = 1.0 ya da hepsi 1) aktif ve seçilebilir
// kaldı. Puan Σ(w·norm)/Σw ile normalize edildiğinden hesap çalışır — kusur tam
// bu yüzden sessizdi. Rozet, ats-applications'taki işe alım rozetiyle AYNI
// desendir (yeni desen icat edilmez).
const WeightFlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: #FDECEA;
  color: #C62828;
`;

const boolFilter = (label, t) => ({
  filter: true,
  sort: false,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useAtsScorecardsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-scorecards?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/ats-scorecards/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('ats.scorecard.name'),
      options: {
        filter: true,
        sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        // Ağırlığı geçersiz şablon ADIN YANINDA işaretlenir: İK listede tek
        // bakışta hangi şablonun kuralı ihlal ettiğini görür. `weightsValid`
        // sunucudan gelir (AtsScorecardResponseDto); ALAN YOKSA (bayat önbellek
        // / eski sürüm) rozet ÇİZİLMEZ — `=== false` bilerek kullanılır,
        // `!row.weightsValid` her satırı kırmızıya boyardı.
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || row.weightsValid !== false) return v || '-';
          const sum = typeof row.weightSum === 'number' ? row.weightSum : 0;
          const tip = t('ats.scorecard.weightsInvalidTooltip', { sum });
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {v || '-'}
              <WeightFlagBadge title={tip}>{t('ats.scorecard.weightsInvalidBadge')}</WeightFlagBadge>
            </span>
          );
        },
      },
    },
    // EO-36 — ÖLÜ KOLON + ÖLÜ SÜZGEÇ KALDIRILDI. Liste yalnız aktif şablonları
    // döndürdüğü için kolon HER satırda '✓' gösteriyordu (taşıdığı bilgi sıfır),
    // `isActive[eq]=false` süzgeci ise HER ZAMAN boş liste veriyordu — kullanıcı
    // bunu "hiç şablon yok" diye okuyordu. Arka uçtaki `isActive[eq]` parametresi
    // de aynı commit'te kaldırıldı (AtsScorecardController/DTO/DataAccess).
    {
      name: 'isPanelDebriefRequired',
      label: t('ats.scorecard.panelDebrief'),
      options: { ...boolFilter(t('ats.scorecard.panelDebrief'), t), customBodyRender: (v) => v ? '✓' : '-' },
    },
    numericFilterColumn('competencyCount', t('ats.scorecard.competencyCount'), { decimals: 0 }),
    dateFilterColumn('createDate', t('ats.scorecard.createDate'), lang),
  ], [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, remove };
};

export default useAtsScorecardsApi;

// ─── Responses (filled-out scorecards) ─────────────────────────────────────────
// Lists actual evaluations across the tenant. Joined with Interview, Application,
// Candidate, JobPosting on the BE so the table can render the full relationship
// without N+1 calls.
export const useAtsScorecardResponsesApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-scorecards/responses?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const recommendationFilter = useMemo(() => ({
    filter: true,
    sort: true,
    filterType: 'custom',
    filterOptions: {
      defaultOp: 'in',
      display: (filterList, onChange, index, column) => {
        const current = filterList[index] && filterList[index][0]
          ? filterList[index][0].split(',').filter(Boolean)
          : [];
        return (
          <FormControl variant="standard" fullWidth>
            <InputLabel>{t('ats.scorecard.overallRecommendation')}</InputLabel>
            <Select
              multiple
              value={current}
              onChange={(e) => {
                const v = e.target.value;
                const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
                filterList[index] = csv ? [csv] : [];
                onChange(filterList[index], index, column);
              }}
              renderValue={(selected) => selected.map((s) => t(`ats.scorecard.recommendation.${s}`, { defaultValue: s })).join(', ')}
            >
              {RECOMMENDATION_KEYS.map((o) => (
                <MenuItem key={o} value={o}>{t(`ats.scorecard.recommendation.${o}`, { defaultValue: o })}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'applicationId', label: 'AppId', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'interviewId', label: 'InterviewId', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'jobPostingId', label: 'JobPostingId', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'scorecardName',
      label: t('ats.scorecard.name'),
      options: { filter: false, sort: false },
    },
    {
      name: 'interviewSummary',
      label: t('ats.scorecard.interview'),
      options: { filter: false, sort: false },
    },
    {
      name: 'candidateFullName',
      label: t('ats.scorecard.candidate'),
      options: { filter: false, sort: false },
    },
    {
      // FE-28 — İK bir panelde kimin skorkart verdiğini göremiyordu: DTO yalnız
      // EvaluatorUserId taşıyordu. Ad sunucuda kimlik ailesiyle çözülür; boş
      // gelirse (silinmiş kişi) tire çizilir — ham id ASLA gösterilmez.
      name: 'evaluatorName',
      label: t('ats.scorecard.evaluator'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'jobPostingTitle',
      label: t('ats.scorecard.jobPosting'),
      options: { filter: false, sort: false },
    },
    {
      name: 'overallRecommendation',
      label: t('ats.scorecard.overallRecommendation'),
      options: {
        ...recommendationFilter,
        customBodyRender: (v) => t(`ats.scorecard.recommendation.${v}`, { defaultValue: v }),
      },
    },
    numericFilterColumn('overallScore', t('ats.scorecard.overallScore'), { decimals: 2 }),
    dateFilterColumn('submittedAt', t('ats.scorecard.submittedAt'), lang, { includeTime: true }),
  ], [t, lang, recommendationFilter]);

  return { fetching, data, pagination, columns, refresh };
};
